import type { FC, ReactNode } from 'react'

import { Button } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import classNames from 'classnames'

import { Table } from 'common/components/Table/Table'
import {
  UNASSIGNED_SCHEDULE_STAFF_ID,
  getScheduleStaffColor,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import { Avatar } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/providerShared'
import { toUsDate } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/seriesDates'
import { SERIES_TEXT_V2 } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/seriesText'

import type { SeriesListRow } from '../../api/series.types'
import { StatusPill, type StatusPillTone } from '../shared/StatusPill'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'
import styles from './seriesListPage.module.scss'

const TEXT = SERIES_PAGES_TEXT.list
const ACTIVE = 'active'

// Board grid widths plus the 12px gap, edge cells plus the 18px inset; Patient..Actions, Provider fills.
const WIDTHS = [224, undefined, 182, 132, 172, 122, 122, 174] as const

type SeriesListTableProps = {
  rows: SeriesListRow[]
  busyId: number | null
  detailsId: number | null
  onDetails: (row: SeriesListRow) => void
  onCancel: (row: SeriesListRow) => void
}

const PILLS = new Map<string, { tone: StatusPillTone; label: string }>([
  ['active', { tone: 'active', label: TEXT.pillActive }],
  ['completed', { tone: 'done', label: TEXT.pillCompleted }],
  ['cancelled', { tone: 'muted', label: TEXT.pillCancelled }],
])

const clampPct = (pct: number): number => Math.min(100, Math.max(0, Number.isFinite(pct) ? pct : 0))

const PatientCell: FC<{ row: SeriesListRow }> = ({ row }) => (
  <div className={styles.stack}>
    <span className={styles.strong}>{row.patient.name ?? TEXT.unknownPatient}</span>
    {row.patient.emrId !== null && (
      <span className={styles.sub}>{TEXT.pid(row.patient.emrId)}</span>
    )}
  </div>
)

const ProviderCell: FC<{ row: SeriesListRow }> = ({ row }) => {
  const { id, name } = row.doctor
  const staffId = id ?? UNASSIGNED_SCHEDULE_STAFF_ID
  return (
    <div className={styles.provider}>
      {name !== null && (
        <Avatar person={{ id: staffId, name, role: '', color: getScheduleStaffColor(staffId) }} />
      )}
      <span className={styles.ellipsis}>{name ?? SERIES_TEXT_V2.unknownProvider}</span>
    </div>
  )
}

const ProgressCell: FC<{ row: SeriesListRow }> = ({ row }) => {
  const pct = clampPct(row.progressPct)
  return (
    <div className={styles.progress}>
      <span
        className={styles.track}
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}>
        <span className={styles.fill} style={{ width: `${pct}%` }} />
      </span>
      <span className={styles.subText}>{TEXT.progressLabel(row.completed, row.total)}</span>
    </div>
  )
}

const StatusCell: FC<{ row: SeriesListRow }> = ({ row }) => {
  const pill = PILLS.get(row.status) ?? { tone: 'muted', label: row.status }
  return <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
}

const buildColumns = ({
  busyId,
  detailsId,
  onDetails,
  onCancel,
}: Omit<SeriesListTableProps, 'rows'>): ColumnsType<SeriesListRow> => {
  const cells: ((row: SeriesListRow) => ReactNode)[] = [
    (row) => <PatientCell row={row} />,
    (row) => <ProviderCell row={row} />,
    (row) => <span className={styles.text}>{row.name}</span>,
    (row) => (
      <span className={styles.cadence}>
        {row.cadenceLabel
          ? TEXT.cadenceTimes(row.cadenceLabel, row.total)
          : TEXT.visitsLabel(row.total)}
      </span>
    ),
    (row) => <ProgressCell row={row} />,
    (row) => (
      <span className={styles.text}>
        {row.nextVisit ? toUsDate(row.nextVisit.date) : TEXT.noDate}
      </span>
    ),
    (row) => <StatusCell row={row} />,
    (row) => (
      <div className={styles.actions}>
        <Button
          size='small'
          className={classNames(styles.small, styles.primary)}
          loading={detailsId === row.id}
          onClick={() => onDetails(row)}>
          {TEXT.details}
        </Button>
        {row.status === ACTIVE && (
          <Button
            size='small'
            className={styles.small}
            loading={busyId === row.id}
            disabled={busyId === row.id}
            onClick={() => onCancel(row)}>
            {TEXT.cancel}
          </Button>
        )}
      </div>
    ),
  ]
  return TEXT.columns.map((title, index) => ({
    key: title,
    title,
    width: WIDTHS[index],
    render: (_: unknown, row: SeriesListRow) => cells[index](row),
  }))
}

export const SeriesListTable: FC<SeriesListTableProps> = ({ rows, ...handlers }) => (
  <Table
    className={styles.table}
    itemKey='id'
    columns={buildColumns(handlers)}
    dataSource={rows}
    isLoading={false}
    haveActions={false}
    pagination={false}
    rowClassName={(row: SeriesListRow) => (row.status === ACTIVE ? '' : styles.mutedRow)}
  />
)
